export function formatFeeReportAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Average monthly fee per enrolled student (total headcount, includes free). */
export function computeAverageFee(feeTotal, totalStudents) {
  const count = Number(totalStudents) || 0;
  const total = Number(feeTotal) || 0;
  if (count <= 0) return 0;
  return Math.round((total / count) * 100) / 100;
}

export function sortClassSections(sections) {
  return [...(sections || [])].sort((a, b) => {
    const byClass = String(a.className || "").localeCompare(
      String(b.className || ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
    if (byClass !== 0) return byClass;
    return String(a.sectionName || "").localeCompare(
      String(b.sectionName || ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
}

export function enrichCampusFeeStatus(campus) {
  const classSections = sortClassSections(campus.classSections);
  return {
    ...campus,
    classSections,
    averageRows: classSections.map((row) => ({
      className: row.className,
      sectionName: row.sectionName,
      totalStudents: row.totalStudents,
      freeStudents: row.freeStudents,
      feeStatusStudents: row.feeStatusStudents,
      feeTotal: row.feeTotal,
      averageFee: computeAverageFee(row.feeTotal, row.totalStudents),
    })),
    campusAverageFee: computeAverageFee(
      campus.grandSumMonthlyFee ?? campus.feeTotal,
      campus.grandTotalStudents ?? campus.totalStudents
    ),
  };
}

export function normalizeFeeStatusResponse(body) {
  if (!body || body.status !== "success") {
    return { campuses: [], message: body?.message || "Failed to load report." };
  }
  const list = Array.isArray(body.data) ? body.data : [];
  return {
    campuses: list.map(enrichCampusFeeStatus),
    message: "",
  };
}

export function buildFeeStatusExcelRows(campuses) {
  const feeStatusRows = [];
  const averageRows = [];

  (campuses || []).forEach((campus) => {
    const campusLabel = campus.branch || campus.campusName || "Campus";

    feeStatusRows.push({
      Campus: campusLabel,
      Class: "",
      Section: "",
      "Total Students": "",
      "Free Students": "",
      "Fee Status Students": "",
      "Monthly Fee Total": "",
    });

    (campus.classSections || []).forEach((row, index) => {
      feeStatusRows.push({
        Campus: index === 0 ? campusLabel : "",
        Class: row.className || "",
        Section: row.sectionName || "",
        "Total Students": Number(row.totalStudents) || 0,
        "Free Students": Number(row.freeStudents) || 0,
        "Fee Status Students": Number(row.feeStatusStudents) || 0,
        "Monthly Fee Total": Number(row.feeTotal) || 0,
      });
    });

    feeStatusRows.push({
      Campus: "",
      Class: "Campus Total",
      Section: "",
      "Total Students":
        Number(campus.grandTotalStudents ?? campus.totalStudents) || 0,
      "Free Students":
        Number(campus.grandTotalFreeStudents ?? campus.freeStudents) || 0,
      "Fee Status Students":
        Number(campus.grandTotalFeeStatusStudents ?? campus.feeStatusStudents) ||
        0,
      "Monthly Fee Total":
        Number(campus.grandSumMonthlyFee ?? campus.feeTotal) || 0,
    });
    feeStatusRows.push({});

    averageRows.push({
      Campus: campusLabel,
      Class: "",
      Section: "",
      "Total Students": "",
      "Free Students": "",
      "Fee Status Students": "",
      "Total Monthly Fee": "",
      "Average Monthly Fee": "",
    });

    (campus.averageRows || []).forEach((row, index) => {
      averageRows.push({
        Campus: index === 0 ? campusLabel : "",
        Class: row.className || "",
        Section: row.sectionName || "",
        "Total Students": Number(row.totalStudents) || 0,
        "Free Students": Number(row.freeStudents) || 0,
        "Fee Status Students": Number(row.feeStatusStudents) || 0,
        "Total Monthly Fee": Number(row.feeTotal) || 0,
        "Average Monthly Fee": Number(row.averageFee) || 0,
      });
    });

    averageRows.push({
      Campus: "",
      Class: "Campus Total",
      Section: "",
      "Total Students":
        Number(campus.grandTotalStudents ?? campus.totalStudents) || 0,
      "Free Students":
        Number(campus.grandTotalFreeStudents ?? campus.freeStudents) || 0,
      "Fee Status Students":
        Number(campus.grandTotalFeeStatusStudents ?? campus.feeStatusStudents) ||
        0,
      "Total Monthly Fee":
        Number(campus.grandSumMonthlyFee ?? campus.feeTotal) || 0,
      "Average Monthly Fee": Number(campus.campusAverageFee) || 0,
    });
    averageRows.push({});
  });

  return { feeStatusRows, averageRows };
}
